// CompanyFieldRowActions — the company-field row kebab: Edit · Duplicate ·
// Hide/Show, the same three the selection bar offers (a kebab is inherently a
// single-row context). Every callback is OPTIONAL: the page passes `undefined`
// when the verb (edit / create / edit) is missing and the item doesn't render —
// absence means hidden. There is no read item, so with none of the three the
// kebab renders nothing at all. Its own component rather than inline in the
// column's `render` because it owns the dropdown's open state, and hooks can't
// live in a render callback.
//
// `portal` is required: the table scrolls horizontally, so an in-flow panel
// would be clipped by that container. Clicks stopPropagation so a menu click
// stays inside the kebab and never reaches the surrounding row.

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Dropdown, Icon, IconButton } from '@/components/common';
import type { CompanyField } from '@/api/endpoints/companyFieldsApi';

interface CompanyFieldRowActionsProps {
  field: CompanyField;
  /** Absent = hidden. */
  onEdit?: (field: CompanyField) => void;
  /** Absent = hidden. */
  onDuplicate?: (field: CompanyField) => void;
  /** Absent = hidden. */
  onToggleStatus?: (field: CompanyField) => void;
}

const CompanyFieldRowActions = ({
  field,
  onEdit,
  onDuplicate,
  onToggleStatus,
}: CompanyFieldRowActionsProps) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  // Close the menu, then run the action.
  const pick = (run: (f: CompanyField) => void) => (e: React.MouseEvent) => {
    e.stopPropagation();
    setOpen(false);
    run(field);
  };

  if (!onEdit && !onDuplicate && !onToggleStatus) return null;

  return (
    <Dropdown
      open={open}
      onClose={() => setOpen(false)}
      align='end'
      width={200}
      portal
      trigger={
        <IconButton
          icon='ellipsis-vertical'
          variant='ghost'
          size='sm'
          aria-haspopup='menu'
          aria-expanded={open}
          aria-label={t('data_structure_cf_row_actions')}
          onClick={(e) => {
            e.stopPropagation();
            setOpen((o) => !o);
          }}
        />
      }
    >
      {onEdit && (
        <button type='button' className='app-dropdown__item' role='menuitem' onClick={pick(onEdit)}>
          <Icon name='pencil' size={15} />
          <span className='app-dropdown__item-label'>{t('data_structure_cf_action_edit')}</span>
        </button>
      )}
      {onDuplicate && (
        <button
          type='button'
          className='app-dropdown__item'
          role='menuitem'
          onClick={pick(onDuplicate)}
        >
          <Icon name='copy' size={15} />
          <span className='app-dropdown__item-label'>
            {t('data_structure_cf_action_duplicate')}
          </span>
        </button>
      )}
      {onToggleStatus && (
        <button
          type='button'
          className='app-dropdown__item'
          role='menuitem'
          onClick={pick(onToggleStatus)}
        >
          <Icon name={field.isHidden ? 'eye' : 'eye-off'} size={15} />
          <span className='app-dropdown__item-label'>
            {t(field.isHidden ? 'data_structure_cf_action_show' : 'data_structure_cf_action_hide')}
          </span>
        </button>
      )}
    </Dropdown>
  );
};

export default CompanyFieldRowActions;
